import {
  DrawbackItem,
  EmenuBlogsTranslation,
  EmenuSample,
} from "@definitions/types"
import { routing } from "@i18n/routing"

type TypeName = DrawbackItem[] | EmenuSample[]

type ObjectType<T> = T extends DrawbackItem[]
  ? DrawbackItem[]
  : T extends EmenuSample[]
    ? EmenuSample[]
    : never

export function partitionArray<T extends TypeName>(
  array: T,
  size: number
): ObjectType<T>[] {
  return array
    .map((e, i) => (i % size === 0 ? array.slice(i, i + size) : null))
    .filter((e) => e) as ObjectType<T>[]
}

export const isDevEnvironment =
  process && process.env.NODE_ENV === "development"

export const titleCase = (str: string) => {
  return str
    .toLowerCase()
    .split(" ")
    .map(function (word) {
      return word.replace(word[0], word[0].toUpperCase())
    })
    .join(" ")
}

// Safe-load next-intl messages with fallback to English
export const loadMessages = async (ns: string, locale: string) => {
  try {
    return (await import(`@messages/${ns}/${locale}.json`)).default
  } catch {
    return (await import(`@messages/${ns}/en.json`)).default
  }
}

export const resolveTranslation = (
  translations: EmenuBlogsTranslation[] | null | undefined,
  locale?: string
) => {
  if (!translations?.length) return null

  // exact match
  const exact = translations.find((t) => t.languages_code === locale)
  if (exact) return exact

  // base language (e.g., 'es' for 'es-mx')
  const base = locale?.split("-")?.[0]
  if (base) {
    const baseHit = translations.find((t) => t.languages_code === base)
    if (baseHit) return baseHit
  }

  // English default
  return (
    translations.find((t) => t.languages_code === routing.defaultLocale) ?? null
  )
}

// Luxon likes region in uppercase: 'es-mx' -> 'es-MX'
export function normaliseLuxonLocale(loc?: string) {
  if (!loc) return routing.defaultLocale
  const parts = loc.split("-")
  if (parts.length === 1) return parts[0]
  return `${parts[0]}-${parts[1].toUpperCase()}`
}
